import Countdown from 'react-countdown'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import {
  getLonBalance,
  getStakingCoolDown,
  getStakingCoolDownRemainSeconds,
  getStakingDashboard,
  getXLonBalance,
} from '~/apis'
import IconClock from '~/assets/images/clock.svg'
import DataContainer from '~/components/DataContainer'
import { useBreakpoint, useGA, useUpdaterChainId } from '~/hooks'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber, toBigNumber } from '~/utils/numbers'
import StakingFooter from '../StakingFooter'
import { LON } from '~/constants/addresses'
import {
  StyledDepositBtn,
  StyledPersonalAssetContainer,
  StyledAssetDataContainer,
  StyledConnectWalletContainer,
} from './styled'
import { useAccount } from 'wagmi'
import Tooltip from '~/components/Tooltip'

const PersonalAsset: React.FC = () => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const { address: account } = useAccount()
  const chainId = useUpdaterChainId()

  const { trackElClickCurring } = useGA()
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'staking' },
  })
  const toggleRedemptionModal = useModalToggle(ApplicationModal.LON_REDEMPTION)

  const { data: stakingDashboard } = useSWR(
    ['getStakingDashboard', chainId],
    () => getStakingDashboard(chainId),
  )
  const scaleIndex = stakingDashboard?.scaleIndex

  const { data: lonBalance } = useSWR(['getLonBalance', chainId, account], () =>
    getLonBalance(chainId, account),
  )

  const lonUnitPrice = useTokenPrice(LON[chainId])

  const { data: xLonBalance } = useSWR(
    ['getXLonBalance', chainId, account],
    () => getXLonBalance(chainId, account),
  )
  const { data: coolDown } = useSWR(
    ['getStakingCoolDown', chainId, account],
    () => getStakingCoolDown(chainId, account),
  )
  const { data: remainingSeconds } = useSWR(
    ['getStakingCoolDownRemainSeconds', chainId, account],
    () => getStakingCoolDownRemainSeconds(chainId, account),
  )

  const totalLonPrice = toBigNumber(lonBalance).multipliedBy(
    toBigNumber(lonUnitPrice),
  )
  const reward = toBigNumber(xLonBalance).multipliedBy(scaleIndex)
  const rewardLonPrice = toBigNumber(reward).multipliedBy(
    toBigNumber(lonUnitPrice),
  )
  const isXLonBalanceSmaller = Number(xLonBalance) < 0.01
  const balance = isXLonBalanceSmaller ? '< 0.01' : formatNumber(xLonBalance, 4)

  return (
    <>
      <StyledPersonalAssetContainer className="personal-asset-container">
        <h4 className="asset-title">
          {t('staking_assets_all')}
          <Tooltip
            title={t('lon_holders_enjoy_discount_tips')}
            trigger="hover"
            placement="topLeft"
          />
        </h4>
        {account ? (
          <StyledAssetDataContainer className="asset-data-container">
            <DataContainer
              dataTitle={t('staking_assets_lon_title')}
              description={`${formatNumber(lonBalance, 2)} LON`}
              subDataTitle={`≈$${formatNumber(totalLonPrice, 2)} USD`}
              className="data-container__Lon"
              title=""
              contentStyle={{ paddingRight: 70 }}
            />
            {Number(xLonBalance) > 0 && (
              <DataContainer
                dataTitle={t('staking_assets_xlon_title')}
                description={`${balance} xLON`}
                title={null}
                className="last-container data-container__xLon"
                contentStyle={{ paddingRight: 70 }}
                subDataTitle={
                  <Countdown
                    date={Date.now() + Number(remainingSeconds) * 1000}
                    renderer={({ days, hours, minutes, seconds }) => {
                      if (coolDown == 0) return null
                      return (
                        <span>
                          <img src={IconClock} alt="" />
                          <span className="time">
                            {days > 0 ? `${days} ${t('days')}` : ''} {hours} :{' '}
                            {minutes > 9 ? minutes : `0${minutes}`} :{' '}
                            {seconds > 9 ? seconds : `0${seconds}`}
                          </span>
                        </span>
                      )
                    }}
                    key={remainingSeconds}
                  />
                }
              >
                <StyledDepositBtn
                  className="unlock-btn"
                  data-trackid="webtl_staking_redeem"
                  onClick={(e) => trackElClickCurring(e)(toggleRedemptionModal)}
                >
                  {t('redeem')}
                </StyledDepositBtn>
              </DataContainer>
            )}
            {reward && Number(reward) !== 0 && (
              <DataContainer
                dataTitle={t('current_stake_reward')}
                subDataTitle={`≈$${formatNumber(rewardLonPrice, 2)} USD`}
                description={`${formatNumber(reward, 4)} LON`}
                title={t('current_stake_reward_tip')}
                className="last-container data-container__xLon"
                contentStyle={{ paddingRight: 70 }}
              />
            )}
          </StyledAssetDataContainer>
        ) : (
          <StyledConnectWalletContainer>
            <a
              className="link"
              data-trackid="webtl_walletconnect"
              data-trackdata={JSON.stringify({ from: 'staking' })}
              onClick={(e) => trackElClickCurring(e)(toggleWalletModal)}
              rel="noopener noreferrer"
            >
              {t('connect')}&nbsp;
            </a>
            <span className="check-data">{t('to_view_data')}</span>
          </StyledConnectWalletContainer>
        )}
      </StyledPersonalAssetContainer>
      {isMobile && (
        <StakingFooter
          onRedeem={toggleRedemptionModal}
        />
      )}
    </>
  )
}

export default PersonalAsset
